import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, UserCheck, PackageSearch, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoyaltyReconciliation = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [summary, setSummary] = useState([]);
  const [total, setTotal] = useState(0);
  const [authors, setAuthors] = useState([]);
  const [selected, setSelected] = useState({}); // {isbn: author_id}
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState({});
  const [reprocessing, setReprocessing] = useState(false);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/admin/pending-royalty-sales`, { headers }),
        axios.get(`${API}/admin/royalties/authors`, { headers }),
      ]);
      setSummary(p.data.summary_by_isbn || []);
      setTotal(p.data.total || 0);
      setAuthors(a.data.authors || []);
      // Pre-seleccionar autor sugerido por nombre del reporte
      const presel = {};
      (p.data.summary_by_isbn || []).forEach((row) => {
        if (row.suggested_author_id) presel[row._id] = row.suggested_author_id;
      });
      setSelected((prev) => ({ ...presel, ...prev }));
    } catch (e) {
      toast.error('Error al cargar ventas pendientes');
    } finally {
      setLoading(false);
    }
  };

  const assign = async (isbn) => {
    const author_id = selected[isbn];
    if (!author_id) return toast.error('Selecciona un autor');
    setAssigning((s) => ({ ...s, [isbn]: true }));
    try {
      const res = await axios.post(`${API}/admin/assign-pending-sales`, { isbn, author_id }, { headers });
      toast.success(`${res.data.sales_assigned} venta(s) asignada(s) correctamente`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al asignar');
    } finally {
      setAssigning((s) => ({ ...s, [isbn]: false }));
    }
  };

  const reprocess = async () => {
    setReprocessing(true);
    try {
      const res = await axios.post(`${API}/admin/publicala/reprocess-royalties`, {}, { headers });
      const r = res.data.results || {};
      toast.success(`Reprocesadas ${res.data.processed} ventas · Acreditadas: ${r.credited || 0}, Pendientes: ${r.pending_reconciliation || 0}`);
      refresh();
    } catch (e) {
      toast.error('Error al reprocesar');
    } finally {
      setReprocessing(false);
    }
  };

  const fmt = (n) => `$${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`;
  const fmtCur = (n, cur) => `${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

  return (
    <div className="space-y-6" data-testid="royalty-reconciliation-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
            <PackageSearch className="w-5 h-5 text-orange-400" /> Conciliación de Regalías
          </h2>
          <p className="verdana-regular text-white/60 text-sm">
            Ventas de app.viubux.com cuyo ISBN no está asociado a un autor. Asígnalas al autor correcto.
          </p>
        </div>
        <Button data-testid="reprocess-royalties-btn" onClick={reprocess} disabled={reprocessing}
          variant="outline" className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10">
          <RefreshCw className={`w-4 h-4 mr-2 ${reprocessing ? 'animate-spin' : ''}`} />
          {reprocessing ? 'Reprocesando...' : 'Reprocesar Ventas'}
        </Button>
      </div>

      {loading ? (
        <p className="text-white/60">Cargando...</p>
      ) : summary.length === 0 ? (
        <Card className="card-viubux-dark border-white/10" data-testid="reconciliation-empty">
          <CardContent className="p-8 text-center text-white/60">
            <Inbox className="w-10 h-10 mx-auto mb-3 text-white/30" />
            No hay ventas pendientes de conciliación. Todo está asignado. 🎉
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="text-white/70 text-sm">{total} venta(s) pendiente(s) en {summary.length} ISBN(s)</div>
          {summary.map((row) => (
            <Card key={row._id} className="card-viubux-dark border-orange-500/20" data-testid={`reconcile-isbn-${row._id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="gotham-medium text-white text-base flex items-center justify-between flex-wrap gap-2">
                  <span>{row.work_title || 'Sin título'}</span>
                  <span className="text-orange-400 gotham-bold">{fmt(row.total_net_royalty)}</span>
                </CardTitle>
                <p className="text-white/50 text-xs">ISBN: {row._id} · {row.count} venta(s)</p>
                {row.net_by_currency && row.net_by_currency.length > 0 && (
                  <p className="text-white/40 text-xs mt-0.5" data-testid={`currency-breakdown-${row._id}`}>
                    Neto original: {row.net_by_currency.map((c) => fmtCur(c.net_original, c.currency)).join(' · ')}
                  </p>
                )}
                {row.report_author && (
                  <p className="text-cyan-300/80 text-xs mt-1" data-testid={`report-author-${row._id}`}>
                    👤 Autor en reporte: <strong>{row.report_author}</strong>
                    {row.suggested_author_id ? ' · coincidencia encontrada ✓' : ' · sin coincidencia (asignar manual)'}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3">
                  <select
                    data-testid={`author-select-${row._id}`}
                    value={selected[row._id] || ''}
                    onChange={(e) => setSelected((s) => ({ ...s, [row._id]: e.target.value }))}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-black/40 border border-purple-500/30 text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="">Selecciona un autor...</option>
                    {authors.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.full_name} ({a.email}) · {a.works_count} obra(s)
                      </option>
                    ))}
                  </select>
                  <Button
                    data-testid={`assign-btn-${row._id}`}
                    onClick={() => assign(row._id)}
                    disabled={assigning[row._id]}
                    className="btn-viubux whitespace-nowrap"
                  >
                    <UserCheck className="w-4 h-4 mr-2" />
                    {assigning[row._id] ? 'Asignando...' : 'Asignar al Autor'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
};

export default RoyaltyReconciliation;
